import { later, next } from '@ember/runloop';
import Service, { service } from '@ember/service';

import pako from 'pako';

import type {
	APIData,
	DateRangeInterface,
	DSSetup,
	RequestError,
	Service as ServiceTypes,
	Timeframe,
} from 'webapp-types';
import type { BlockVisualization, ForecastComparisonPeriod, TimeUnit } from 'webapp/enums/enums';
import {
	FakeModelIdPrefix,
	GranulationTypeId,
	MetricDetailsTab,
	TimeUnit as TimeUnitEnum,
} from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import {
	base64UrlToUint8Array,
	copyObject,
	equal,
	uint8ArrayToBase64Url,
	uniqueId,
	utf8_to_b64,
} from 'webapp/utils/common';
import { isNotValidMetricInsightDateRange } from 'webapp/utils/date-range';
import { sortByPopularityPriority } from 'webapp/utils/metric-sort';

import type { GoalInsightsChartState } from 'webapp/components/goals/insights/data-visualization';
import MetricDetailsDrawerPopupComponent from 'webapp/components/metric-details/drawer-popup';
import { anomalyConfigurationInitialValues } from 'webapp/components/metric-details/explore/data';
import type { MetricInsightsChartState } from 'webapp/components/metric-details/explore/data';
import type { AnomalyConfiguration } from 'webapp/components/metric-details/explore/data/anomaly-detection/popup-configuration';

import type { Metric } from 'webapp/models-custom';
import type Block from 'webapp/models-custom/block';
import DateRange from 'webapp/models-custom/date-range';
import type { ForecastMethodology } from 'webapp/models-custom/forecast';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type PopupModal from 'webapp/models-custom/popup/modal';
import type VisualizationBase from 'webapp/models-custom/visualization/base';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

import type { RSjqXHR } from 'webapp/services/request';

export interface SharedMetricDetailsState {
	metricSetup: DSSetup.MetricSetupData;
}

export interface ExploreTabState {
	dateRange: DateRangeInterface.FormulaInterface | null;
	visualization: BlockVisualization | null;
	compare?: DSSetup.Compare | null;
	anomalyEnabled: boolean | null;
	anomalyConfig: AnomalyConfiguration | null;
}

export interface ForecastTabState {
	methodology?: ForecastMethodology | null;
	seasonality?: TimeUnit[];
	timeframe?: Timeframe | null;
	endDate?: string | null;
	granulation?: TimeUnit;
	comparisonPeriod?: ForecastComparisonPeriod;
	holidaysCountry?: string | null;
	forecastId?: string | null;
	requestId?: string | null;
}

export interface NormalizedDetailState {
	shared: SharedMetricDetailsState;
	tab: MetricDetailsTab;
	explore?: ExploreTabState;
	forecast?: ForecastTabState;
}

export interface OpenMetricDetailsOptions {
	metricSetup?: MetricSetup;
	tab?: MetricDetailsTab;
	dateRange?: DateRangeInterface.FormulaInterface;
	visualization?: BlockVisualization;
	forecast?: ForecastTabState;
	// A full state (e.g. from a shared URL) wins over the options above.
	state?: NormalizedDetailState;
}

// ---- Explore types ----

export interface ColoredMetricSetupData extends DSSetup.MetricSetupWithoutFiltersData {
	colorIndex: number;
	colorHex?: string;
	metricName?: string;
	attributeName?: string;
	unitName?: string;
}

export default class MetricDetailsService extends Service {
	@service('popup') declare popupService: ServiceTypes.Popup;
	@service('event-manager') declare eventManagerService: ServiceTypes.EventManager;
	@service('billing') declare billingService: ServiceTypes.Billing;
	@service('request') declare requestService: ServiceTypes.Request;
	@service('analytics') declare analyticsService: ServiceTypes.Analytics;
	@service('account') declare accountService: ServiceTypes.Account;
	@service('model-manager/metrics') declare mmMetricsService: ServiceTypes.ModelManager.Metrics;
	@service('infolayer') declare infolayerService: ServiceTypes.Infolayer;
	@service('store') declare storeService: ServiceTypes.Store;
	@service('navigation') declare navigationService: ServiceTypes.Navigation;

	// ---- Mode ----

	// In the native app's webview the drawer only explores: nothing in it writes, leaves it or upsells.
	get isMobileMode(): boolean {
		return this.navigationService.isMobileApp;
	}

	get showForecasts(): boolean {
		return this.billingService.forecastsEnabled && !this.isMobileMode;
	}

	// ---- Navigation ----

	get isActivatedRouteExplorer(): boolean {
		return this.popupService.isOpen(MetricDetailsDrawerPopupComponent);
	}

	// Opens over the current page, or switches the open drawer. Without a metric it shows its "choose a metric" state.
	// Returns the drawer it opened: none when it switched the open one instead.
	openMetricDetailsDrawer(
		options: OpenMetricDetailsOptions = {},
		origin?: string,
	): PopupModal | undefined {
		const state = options.state ?? this.buildStateFromOptions(options);
		// a shared link's state is decoded unvalidated
		const metricSetup =
			options.metricSetup ??
			(state.shared?.metricSetup?.key
				? new MetricSetup(state.shared.metricSetup)
				: undefined);

		const isDrawerOpen = this.popupService.isOpen(MetricDetailsDrawerPopupComponent);
		if (isDrawerOpen) {
			this.eventManagerService.trigger(AppEvent.METRIC_INSIGHTS_SWITCH_METRIC, {
				metricSetup,
				state,
			});
			this.trackIfNeeded(metricSetup, origin);
			if (origin) {
				this.trackMetricInsightsDrawerPopupOpened(origin);
			}
			return undefined;
		}

		const metric = metricSetup?.metric;
		const title = metric
			? `Insights for ${metric.name} (${metric.sourceName || 'Unknown connection'})`
			: 'Metric insights';

		const popup = this.popupService.createModal(MetricDetailsDrawerPopupComponent, {
			popupClass: 'popup-type-drawer popup-type-metric-details-drawer',
			showClose: false,
			hasOverlay: true,
			closeOnOverlayClick: false,
			allowStacked: true,
			isAutoCentered: false,
		});

		popup.open({
			title,
			state,
			origin,
		});

		this.trackIfNeeded(metricSetup, origin);
		if (origin) {
			this.trackMetricInsightsDrawerPopupOpened(origin);
		}

		return popup;
	}

	// Switches the open drawer to the most popular connected metric; without one it keeps its empty state.
	selectDefaultMetric(): void {
		// the mobile drawer shows only the metric it was opened for
		if (this.isMobileMode) {
			return;
		}

		const eligibleMetrics = sortByPopularityPriority(
			this.mmMetricsService.metricsByPopularityConnected,
			this.accountService.metadata,
		);

		const topMetric = eligibleMetrics[0];
		if (!topMetric?.metricData || !this.isActivatedRouteExplorer) {
			return;
		}

		this.openMetricDetailsDrawer({ metricSetup: new MetricSetup(topMetric.metricData) });
	}

	// ---- Serialization ----

	serialize(state: NormalizedDetailState): string {
		const json = JSON.stringify(state);
		const compressed = pako.deflate(json);
		return uint8ArrayToBase64Url(compressed);
	}

	deserialize(encoded: string): NormalizedDetailState | null {
		return this.decode(encoded) as NormalizedDetailState | null;
	}

	// Mobile deeplinks (/m/metrics?state=…) encode only the metric setup, the same way as a full state.
	deserializeMetricSetup(encoded: string): DSSetup.MetricSetupData | null {
		const metricSetupData = this.decode(encoded) as Partial<DSSetup.MetricSetupData> | null;

		return typeof metricSetupData?.key === 'string' && metricSetupData.key
			? (metricSetupData as DSSetup.MetricSetupData)
			: null;
	}

	buildShareablePath(state: NormalizedDetailState): string {
		const encoded = this.serialize(state);
		return `/metrics/details?state=${encodeURIComponent(encoded)}`;
	}

	buildShareableUrl(state: NormalizedDetailState): string {
		return `${window.location.origin}${this.buildShareablePath(state)}`;
	}

	buildShareablePathFromOptions(options: OpenMetricDetailsOptions): string {
		return this.buildShareablePath(this.buildStateFromOptions(options));
	}

	buildShareableUrlFromOptions(options: OpenMetricDetailsOptions): string {
		return `${window.location.origin}${this.buildShareablePathFromOptions(options)}`;
	}

	buildShareableUrlFromKeyAndSpaceAccessId(key: string, spaceAccessId: number): string {
		return this.buildShareableUrl({
			shared: { metricSetup: { key, spaceAccessId } },
			tab: MetricDetailsTab.EXPLORE,
			explore: {
				dateRange: null,
				visualization: null,
				anomalyEnabled: false,
				anomalyConfig: anomalyConfigurationInitialValues,
			},
		});
	}

	// ---- Explore ----

	generateMetricInsightsUUID() {
		return -1 * Number(`${FakeModelIdPrefix.METRIC_INSIGHTS}${uniqueId()}`);
	}

	isMetricInsightsTemporaryId(id = '') {
		const fakeModelPrefix = FakeModelIdPrefix.METRIC_INSIGHTS;
		const length = fakeModelPrefix.length;
		return !!id && id.substring(1, 1 + length) === fakeModelPrefix;
	}

	getSetupsFromMetricInsightsVisualizationData(
		visualization?: VisualizationBase,
		metricSetupData?: DSSetup.MetricSetupWithoutFiltersData, // this param is needed temporarily bcos point.metricSelectionData does not have metric info (key, spaceAccessId, dimensions)
	): ColoredMetricSetupData[] {
		let points: DChartPrimaryPoint[] = [];

		if (visualization) {
			points = visualization.getAllMainPoints();
		}

		return points.map((point) => {
			const metricSetup = new MetricSetup(metricSetupData);

			metricSetup.setFiltersFromDimensionsAndValuesMapAndUnit(
				point.metricSelectionData?.dimensionValues || {},
			);
			metricSetup.unitName = point.metricSelectionData?.unit || null;

			return Object.assign(
				{
					key: '',
					spaceAccessId: null,
					dimensions: [],
					metricName: metricSetup.metric?.name || '',
					colorIndex: point.colorIndexOrClass as number,
					attributeName: metricSetup.firstAttributeNameFromBasicFilters,
					unitName: metricSetup.unitName,
				},
				copyObject(metricSetup.metricSetupData || {}),
			);
		});
	}

	getColorHexForSelection(
		metricSetup: MetricSetup,
		coloredMetricSetupDataItems: ColoredMetricSetupData[],
	) {
		let coloredSelection = null;

		if (coloredMetricSetupDataItems.length) {
			if (metricSetup.metric) {
				const mustSelectAttribute = metricSetup.allowBasicDimensionValueFilters,
					mustSelectUnit = metricSetup.hasUnits;

				let coloredSelections = coloredMetricSetupDataItems.filter((item) => {
					return (
						item.key === metricSetup.key &&
						item.spaceAccessId === metricSetup.spaceAccessId &&
						equal(item.dimensions, metricSetup.dimensions)
					);
				});

				if (mustSelectAttribute) {
					coloredSelections = coloredSelections.filter((item) => {
						return (
							(!item.attributeName &&
								!metricSetup.firstAttributeNameFromBasicFilters) ||
							item.attributeName === metricSetup.firstAttributeNameFromBasicFilters
						);
					});
				}

				if (mustSelectUnit) {
					coloredSelections = coloredSelections.filter((item) => {
						return (
							(!item.unitName && !metricSetup.unitName) ||
							item.unitName === metricSetup.unitName
						);
					});
				}

				coloredSelection = coloredSelections?.[0] || null;
			}
		}

		return coloredSelection?.colorHex || '';
	}

	fetchVisualizationModuleData(block: Block) {
		const data = {
			dataSelects: block.dataSelects.map((ds) => ds.toSerializedJSON(true)),
			block: block.toSerializedJSON(true),
		};

		return this.requestService.api<APIData.Block.SampleResponse>(
			'POST',
			'/blocks/sample',
			data,
			{
				timeout: 60 * 1000,
			},
		);
	}

	requestVisualizationModuleData(
		block: Block,
		chartState: MetricInsightsChartState | GoalInsightsChartState,
		blockUUID: string,
		visualizationModuleDataXHR: RSjqXHR | null,
		retryCount = 0,
	): RSjqXHR | null {
		if (!block.dataSelects.length) {
			return null;
		}

		// if request already in progress, cancel it
		if (visualizationModuleDataXHR && visualizationModuleDataXHR.readyState !== 4) {
			visualizationModuleDataXHR.abort();
		}

		// A fresh request claims the chart; a retry inherits the claim of the request that
		// started it.
		if (retryCount === 0) {
			chartState.isHorizonPollingExhausted = false;
			chartState.activeBlockUUID = blockUUID;
		}

		// A timeframe or metric change builds a new block and claims the chart, which can
		// happen while this request or one of its horizon retries is still in flight. The
		// chart state is shared, so a request for a block that is no longer on screen must
		// not write loading or empty flags into it.
		const isForBlockStillOnScreen = () => chartState.activeBlockUUID === blockUUID;

		const promise = this.fetchVisualizationModuleData(block);

		const xhr = (promise.jqPromise as RSjqXHR) ?? null;
		promise
			.then((reply) => {
				reply.id = blockUUID;
				this.storeService.pushPayload({
					samples: [
						{
							id: blockUUID,
							sampledata: reply,
						},
					],
				});

				next(() => {
					if (chartState && isForBlockStillOnScreen()) {
						const isHorizon =
							block?.dataSelects?.[0]?.metricSetup?.metric?.isTypeHorizon;
						const maxRetries = 6;
						const shouldRetry =
							isHorizon && !!block && block.isDataEmpty && retryCount < maxRetries;
						if (shouldRetry) {
							later(() => {
								if (!isForBlockStillOnScreen()) {
									return;
								}
								if (block.isDataEmpty) {
									this.requestVisualizationModuleData(
										block,
										chartState,
										blockUUID,
										null,
										retryCount + 1,
									);
								} else {
									chartState.isVisualizationModuleDataLoading = false;
								}
							}, 5000);
						} else {
							chartState.isVisualizationModuleDataLoading = false;
							// only while the first sync runs; a completed sync without rows is just empty
							const isFirstSyncRunning = block.statusObjects.some(
								(so) => so.isFirstSync,
							);
							if (
								isHorizon &&
								block.isDataEmpty &&
								retryCount >= maxRetries &&
								isFirstSyncRunning
							) {
								chartState.isHorizonPollingExhausted = true;
							}
						}
						chartState.isDataEmpty = !!block && block.isDataEmpty && !block.isSyncing;
					}
				});
			})
			.catch((error: RequestError) => {
				this.requestService.showAllValidationErrors(error);
				if (error.obj!.statusText !== 'abort') {
					if (chartState && isForBlockStillOnScreen()) {
						chartState.isVisualizationModuleDataLoading = false;
					}
				}
			});

		return xhr;
	}

	async validateDateRange(
		dateRangeFormula: DateRangeInterface.FormulaInterface | null,
		metricData?: DSSetup.MetricData,
	): Promise<DateRange | null> {
		if (dateRangeFormula) {
			if (!isNotValidMetricInsightDateRange(dateRangeFormula)) {
				return null;
			}

			const invalidRangeMessage =
				'Invalid date range, switching automatically to last 30 days.';

			if (dateRangeFormula.range) {
				try {
					// wrong granulation, try to recover
					const correctedFormula =
						await this.mmMetricsService.getFormulaWithOptimalValidGranulation({
							metricData: metricData,
							dateRange: dateRangeFormula,
						});
					return DateRange.createFromFormula(correctedFormula);
				} catch {
					this.infolayerService.error(invalidRangeMessage);
				}
			} else {
				this.infolayerService.error(invalidRangeMessage);
			}
		}

		// fallback: default date range Last 30 days (daily granulation)
		return DateRange.createLastX(30, TimeUnitEnum.DAY, GranulationTypeId.DAILY);
	}

	getInsightsParamsHash(
		metricSetup: MetricSetup,
		visualization: number,
		dateRangeId?: DateRangeInterface.Id,
		compare?: DSSetup.Compare | null,
	) {
		const compareSignature = compare === undefined ? '' : JSON.stringify(compare);
		const params = [
			...metricSetup.attributeNamesFromBasicFilters,
			metricSetup.key,
			metricSetup.spaceAccessId,
			metricSetup.dimensions,
			JSON.stringify(metricSetup.filtersData),
			dateRangeId,
			visualization,
			compareSignature,
		].join('');
		return utf8_to_b64(params);
	}

	// ---- Tracking ----

	trackMetricInsightsViewed(origin?: string, metric?: Metric) {
		if (origin && metric) {
			this.analyticsService.event(TrackingEvent.METRIC_INSIGHTS_VIEWED, {
				origin,
				datasource: metric.dataSourceTypeKey,
				metrics: metric.name,
			});
		}
	}

	trackMetricInsightsDrawerPopupOpened(origin?: string) {
		if (origin) {
			this.analyticsService.event(TrackingEvent.METRIC_INSIGHTS_POPUP, {
				origin,
			});
		}
	}

	trackMetricChanged(metric: Metric, origin = 'metrics_detail') {
		this.analyticsService.event(TrackingEvent.METRIC_DETAILS_METRIC_CHANGED, {
			origin,
			datasource: metric.dataSourceTypeKey,
			metrics: metric.name,
		});
	}

	// ---- Private helpers ----

	// Links are decoded unvalidated: the result is whatever JSON was encoded, or null.
	private decode(encoded: string): unknown {
		try {
			const compressed = base64UrlToUint8Array(encoded);
			const json = pako.inflate(compressed, { to: 'string' });
			return JSON.parse(json);
		} catch {
			return null;
		}
	}

	private buildStateFromOptions(options: OpenMetricDetailsOptions): NormalizedDetailState {
		const metricSetupData = options.metricSetup?.metricSetupData;
		const tab = options.tab ?? MetricDetailsTab.EXPLORE;

		const state: NormalizedDetailState = {
			shared: { metricSetup: metricSetupData ?? { key: '', spaceAccessId: null } },
			tab,
		};

		if (tab === MetricDetailsTab.EXPLORE || options.dateRange || options.visualization) {
			state.explore = {
				dateRange: options.dateRange ?? null,
				visualization: options.visualization ?? null,
				// Leave `compare` unset (undefined) for the default open. The data
				// component only overrides its built-in "Previous period" default
				// when args.compare is defined — so omitting the key here keeps the
				// natural default. URL-shared state will carry an explicit value
				// (formula or null for None) which IS applied.
				anomalyEnabled: false,
				anomalyConfig: anomalyConfigurationInitialValues,
			};
		}

		if (options.forecast) {
			state.forecast = options.forecast;
		}

		return state;
	}

	private trackIfNeeded(metricSetup: MetricSetup | undefined, origin?: string): void {
		if (origin) {
			this.trackMetricInsightsViewed(origin, metricSetup?.metric || undefined);
		}
	}
}

declare module '@ember/service' {
	interface Registry {
		'metric-details': MetricDetailsService;
	}
}
